import React, {useContext, useEffect, useState} from 'react';
import {Box, Slider, Typography, Collapse, IconButton, CircularProgress, Card, CardContent, Tooltip, Fade} from '@mui/material';
import {colorPaper} from "../App/theme";
import { isMobile } from "../App/utils";
import { ChatModels } from "../constants";
import { AlertContext } from "../App/App";
import { useInputValue } from "../Context/ChatInputProvider";
import RefreshIcon from "@mui/icons-material/Refresh";
import CancelIcon from "@mui/icons-material/Close";
import {formatScore} from "../utils";
import {Edit, Delete} from "@mui/icons-material";
import {fetchContextItems} from "./ContextItemsUtils";
import {deleteItem} from "../API/items/itemsAPI";
import {useConfirm} from "../Components/ConfirmContext";
import {initDB} from "../Engines/Dexie";

const ContextItemsList = React.memo(({ contextItems, onDeleteItem, KB }) => {
    const showConfirm = useConfirm();
    const handleDelete = async (meta) => {
        const itemId = meta.itemId;
        const confirmed = await showConfirm({
            title: 'Delete Item Confirmation',
            content: `Are you sure you want to delete this item?`
        });
        
        if (!confirmed) return;
        
        try {
            await deleteItem({ itemId, KBData: KB, itemType: meta.itemType });
            onDeleteItem(itemId);
        } catch (error) {
            console.error('Failed to delete item:', error);
        }
    };

    const formatContent = (item) => {
        const { _score, _tokens, ...content } = item;
        return Object.entries(content).map(([key, value]) => ({
            key,
            value: typeof value === 'string' ? value : JSON.stringify(value)
        }));
    };

    return (
        <Box sx={{ maxHeight: 400, overflow: 'auto', mt: 2 }}>
            {contextItems.map(({ item, meta }) => {
                const contentFields = formatContent({ _score: formatScore(meta.score), _tokens: meta.estimatedTokens, ...item });

                return (
                    <Card 
                        key={item.itemId} 
                        sx={{ 
                            mb: 1.5, 
                            position: 'relative',
                            '&:hover': {
                                boxShadow: 2,
                                '& .action-buttons': {
                                    opacity: 1
                                }
                            },
                            transition: 'box-shadow 0.2s ease'
                        }}
                    >
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    <Typography 
                                        variant="caption" 
                                        sx={{ 
                                            fontSize: '0.65rem', 
                                            color: 'text.secondary',
                                            fontWeight: 500
                                        }}
                                    >
                                        Score: {formatScore(meta.score)}
                                    </Typography>
                                    <Typography 
                                        variant="caption" 
                                        sx={{ 
                                            fontSize: '0.65rem', 
                                            color: 'text.secondary',
                                            fontWeight: 500
                                        }}
                                    >
                                        {meta.estimatedTokens} tokens
                                    </Typography>
                                </Box>
                                <Box 
                                    className="action-buttons" 
                                    sx={{ 
                                        display: 'flex', 
                                        gap: 0.5
                                    }}
                                >
                                    <Tooltip title="Edit item">
                                        <IconButton 
                                            size="small" 
                                            onClick={() => window.open(`/edit-item/${meta.itemId}`, '_blank')} 
                                            sx={{ p: 0.5 }}
                                        >
                                            <Edit fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete item">
                                        <IconButton 
                                            size="small" 
                                            onClick={() => handleDelete(meta)} 
                                            sx={{ p: 0.5 }}
                                        >
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                            
                            {contentFields.map(({ key, value }) => (
                                <Box key={key} sx={{ mb: 0.8, textAlign: 'left' }}>
                                    <Typography 
                                        variant="caption" 
                                        sx={{ 
                                            fontSize: '0.65rem', 
                                            color: 'text.secondary',
                                            fontWeight: 500,
                                            display: 'block',
                                            mb: 0.2,
                                            textAlign: 'left'
                                        }}
                                    >
                                        {key}:
                                    </Typography>
                                    <Typography 
                                        sx={{ 
                                            fontSize: '0.85rem',
                                            color: 'text.primary',
                                            lineHeight: 1.3,
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            textAlign: 'left'
                                        }}
                                    >
                                        {value}
                                    </Typography>
                                </Box>
                            ))}
                        </CardContent>
                    </Card>
                );
            })}
        </Box>
    );
});

export const ContextItems = ({ contextItems, setContextItems, contextWindow, setContextWindow, messages, KB,
                                 isContextItemsOpen, setIsContextItemsOpen, totalTokensSum, setTotalTokensSum,
                                 renderSettings
                             }) => {

    const [panelWidth, setPanelWidth] = useState(100);
    const { setSystemAlert } = useContext(AlertContext);
    const { inputValue, setInputValue } = useInputValue();
    const [loading, setLoading] = useState(false);

    const handleTokenLimitChange = (event, newValue) => setContextWindow(newValue);

    const togglePanel = () => {
        setIsContextItemsOpen(!isContextItemsOpen);
        if (isContextItemsOpen) {
            setTimeout(() => setPanelWidth(100), 300);
        } else {
            setPanelWidth(isMobile ? 'calc(100vw - 60px)' : 800);
        }
    };

    useEffect(() => {
        if (isContextItemsOpen) {
            setPanelWidth(isMobile ? 'calc(100vw - 60px)' : 800);
        }
    }, [isContextItemsOpen]);

    const showConfirm = useConfirm();

    const refreshResults = async () => {
        try {
            setLoading(true)
            const {totalTokens, items} = await fetchContextItems({
                inputValue: inputValue ? inputValue : ' ',
                messages: messages ? messages : [],
                KB,
                contextWindow,
                renderSettings
            })
            setTotalTokensSum(totalTokens)
            setContextItems(items)
            setLoading(false)
        } catch (e) {
            console.error('Unable to fetchContextItems:', e)
            setLoading(false)
        }
    };

    const handleReindex = async () => {
        const confirmed = await showConfirm({
            title: (renderSettings?.contextItemsReindexTitle || 'Reindex All Data Confirmation'),
            content: (renderSettings?.contextItemsReindexContent || 'Are you sure you want to reindex all database items? This will delete and recreate the local database.')
        });

        if (confirmed) {
            try {
                setLoading(true);
                indexedDB.deleteDatabase('IndexedDB_' + KB.kbId).onsuccess = () => {
                    localStorage.removeItem('IndexedDB');
                    initDB({KBData: KB, setSystemAlert, setLoading}).then((db) => {
                        setTimeout(() => {
                            refreshResults();
                        }, 1000);
                    });
                };
            } catch (error) {
                console.error('Failed to reindex:', error);
                setSystemAlert({ msg: 'Failed to reindex database', type: 'error', duration: 5000 });
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        // @Todo to delete this if all works fine
        // if (!inputValue && !messages?.length) return setContextItems([])
        let timeout;
        if (isContextItemsOpen) {
            timeout = setTimeout(refreshResults, 500);
        }
        return () => clearTimeout(timeout);
    }, [inputValue, isContextItemsOpen]);

    const maxContextSize = Math.round(ChatModels?.[KB?.model]?.context * 0.8) || 8000;
    const defaultContextWindow = 8000 // Math.round(ChatModels?.[KB?.model]?.context * 0.2) || 8000;

    useEffect(() => {
        if (!KB?.model) return;

        if (contextWindow > maxContextSize) {
            setSystemAlert({ msg: `Max context items reset to ${defaultContextWindow} tokens`, type: 'info', duration: 5000 });
            setContextWindow(defaultContextWindow);
        }

        if (contextWindow === null) {
            setContextWindow(defaultContextWindow);
        }
    }, [messages, contextWindow, KB, inputValue]);

    if (!contextItems) return null;

    return (
        <Box>
            <Box display="flex" alignItems="center" style={{ cursor: 'pointer' }} onClick={togglePanel}>
                {!renderSettings?.hideContextItems && <Typography fontSize={14}>
                    {contextItems.length} items
                </Typography>}
                {/*<IconButton onClick={togglePanel}>*/}
                {/*    {isContextItemsOpen ? <ArrowDropDown /> : <ArrowDropUp />}*/}
                {/*</IconButton>*/}
            </Box>
            <Collapse in={isContextItemsOpen}>
                <Box 
                    bgcolor={colorPaper} 
                    sx={{ 
                        width: panelWidth, 
                        maxHeight: '70vh',
                        borderRadius: 2,
                        boxShadow: 1,
                        overflow: 'hidden'
                    }}
                >
                    <Box sx={{ 
                        p: 2, 
                        borderBottom: 1, 
                        borderColor: 'divider',
                        bgcolor: 'background.paper'
                    }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Box display="flex" alignItems="center" gap={2}>
                                <IconButton onClick={handleReindex}>
                                    {loading ? <CircularProgress size={24} /> : <RefreshIcon />}
                                </IconButton>
                                
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                    {contextItems?.length} items
                                </Typography>
                            </Box>

                            <IconButton 
                                onClick={() => togglePanel()}
                                sx={{ 
                                    width: 32,
                                    height: 32
                                }}
                            >
                                <CancelIcon fontSize="small" />
                            </IconButton>
                        </Box>
                        
                        <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} alignItems={isMobile ? 'flex-start' : 'center'} gap={2}>
                            <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 'fit-content' }}>
                                {renderSettings?.contextItemsMemoryLabel || 'Memory size'}: {contextWindow} {renderSettings?.contextItemsTokensLabel || 'tokens'}
                            </Typography>
                            <Slider
                                onMouseUp={refreshResults}
                                value={contextWindow}
                                onChange={handleTokenLimitChange}
                                aria-labelledby="token-slider"
                                min={100}
                                max={maxContextSize}
                                step={100}
                                size="small"
                                sx={{ 
                                    width: isMobile ? '100%' : 200,
                                    ml: isMobile ? 0 : 1
                                }}
                            />
                        </Box>
                    </Box>
                    
                    <Box sx={{ 
                        p: 2, 
                        maxHeight: 'calc(70vh - 120px)',
                        overflow: 'auto'
                    }}>
                        <ContextItemsList contextItems={contextItems} onDeleteItem={(itemId) => {
                            setContextItems(contextItems.filter(ci => ci.meta.itemId !== itemId));
                        }} KB={KB} />
                    </Box>
                </Box>
            </Collapse>
        </Box>
    );
};